// CompactTripCard — Condensed trip representation for the trips list view.
//
// Displays: trip name, date range, current stage indicator, next action summary
// (≤80 chars truncated to single line), progress indicator (completed/total stages),
// and alert badges (single badge for one alert, count badge for multiple).
//
// The entire card is tappable and navigates to TripCardFull on tap.
//
// Validates: Requirements 9.1, 9.2, 9.3, 9.4, 9.5, 9.6

import React, { useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { computeCompactCardData } from './computeCompactCardData';
import { buildJourneyEvents } from './buildJourneyEvents';
import { resolveTripStages } from '../../services/journeyStageEngine';
import { colors, radii, typography, fontWeights, shadows } from '../../styles/designTokens';

// ── Styles ──────────────────────────────────────────────────

const cardContainerStyle = {
  backgroundColor: colors.surface,
  border: `1px solid ${colors.divider}`,
  borderRadius: radii.card,
  padding: '16px',
  boxShadow: shadows.card,
  cursor: 'pointer',
  transition: 'box-shadow 0.15s, transform 0.1s',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  position: 'relative',
  width: '100%',
  minHeight: '44px',
};

const cardHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '8px',
};

const tripNameStyle = {
  fontSize: typography.subheading.fontSize,
  fontWeight: fontWeights.semibold,
  color: colors.textPrimary,
  margin: 0,
  lineHeight: '1.3',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
};

const dateRangeStyle = {
  fontSize: typography.caption.fontSize,
  fontWeight: fontWeights.regular,
  color: colors.textSecondary,
  whiteSpace: 'nowrap',
};

const stageIndicatorStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: typography.caption.fontSize,
  fontWeight: fontWeights.medium,
  color: colors.info,
  padding: '2px 8px',
  borderRadius: radii.pill,
  backgroundColor: colors.infoBackground,
};

const nextActionStyle = {
  fontSize: typography.body.fontSize,
  fontWeight: fontWeights.regular,
  color: colors.textSecondary,
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: '1.4',
};

const progressContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const progressBarOuterStyle = {
  flex: 1,
  height: '4px',
  backgroundColor: colors.divider,
  borderRadius: '2px',
  overflow: 'hidden',
};

const progressLabelStyle = {
  fontSize: typography.caption.fontSize,
  fontWeight: fontWeights.medium,
  color: colors.textMuted,
  whiteSpace: 'nowrap',
};

const alertBadgeBaseStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: typography.badge.fontSize,
  fontWeight: fontWeights.semibold,
  borderRadius: radii.pill,
  padding: '2px 8px',
  minWidth: '22px',
  minHeight: '22px',
  lineHeight: '1',
};

// ── Helpers ─────────────────────────────────────────────────

/**
 * Formats a date range as "MMM d – MMM d, yyyy" or "MMM d, yyyy" if same day.
 *
 * @param {{ start: Date, end: Date }} dateRange
 * @returns {string}
 */
function formatDateRange(dateRange) {
  if (!dateRange || !dateRange.start) return '';

  try {
    const start = dateRange.start;
    const end = dateRange.end || start;

    if (start.toDateString() === end.toDateString()) {
      return format(start, 'MMM d, yyyy');
    }

    if (start.getFullYear() === end.getFullYear()) {
      return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
    }

    return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
  } catch {
    return '';
  }
}

/**
 * Returns the alert badge style based on alert type.
 *
 * @param {'critical' | 'warning'} type
 * @returns {Object}
 */
function getAlertBadgeStyle(type) {
  if (type === 'critical') {
    return {
      ...alertBadgeBaseStyle,
      backgroundColor: colors.urgent,
      color: '#ffffff',
    };
  }
  return {
    ...alertBadgeBaseStyle,
    backgroundColor: colors.warning,
    color: '#ffffff',
  };
}

// ── Component ───────────────────────────────────────────────

/**
 * @typedef {Object} CompactTripCardProps
 * @property {Object} trip - Trip data object
 * @property {Record<string, Object>} flightStatuses - Flight status data keyed by flight ID
 * @property {Record<number, Object>} departureCalculations - Departure calculations keyed by flight index
 * @property {(tripId: string) => void} onSelect - Callback when the card is tapped
 */

/**
 * CompactTripCard renders a condensed trip card for the trips list view.
 * The entire card is tappable and navigates to the full trip view.
 *
 * @param {CompactTripCardProps} props
 * @returns {React.ReactElement}
 */
export function CompactTripCard({ trip, flightStatuses, departureCalculations, onSelect }) {
  const tapStartRef = useRef(0);

  // Compute journey events and active stage for the compact card data
  const now = new Date();

  const journeyResult = buildJourneyEvents(trip, now, {
    flightStatuses: flightStatuses || {},
    departureCalculations: departureCalculations || {},
  });
  const events = journeyResult?.events || [];

  const stageResult = resolveTripStages({
    trip,
    flightStatuses: flightStatuses || {},
    departureCalculations: departureCalculations || {},
    now,
  });

  const activeStageId = stageResult?.activeStage?.stage || null;

  const cardData = computeCompactCardData({
    trip,
    events,
    activeStageId,
    activeStageResult: stageResult?.activeStage || null,
    now,
  });

  const handleClick = useCallback(() => {
    if (trip?.tripId && onSelect) {
      onSelect(trip.tripId);
    }
  }, [trip?.tripId, onSelect]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  // Track tap timing for performance (Req 9.2: navigate within 300ms)
  const handlePointerDown = useCallback(() => {
    tapStartRef.current = performance.now();
  }, []);

  const handlePointerUp = useCallback(() => {
    handleClick();
  }, [handleClick]);

  // Compute progress bar width
  const progressPercent = cardData.progress.total > 0
    ? (cardData.progress.completed / cardData.progress.total) * 100
    : 0;

  const progressBarInnerStyle = {
    height: '100%',
    width: `${progressPercent}%`,
    backgroundColor: progressPercent === 100 ? colors.success : colors.info,
    borderRadius: '2px',
    transition: 'width 0.3s ease',
  };

  // Determine alert badge rendering
  const alertBadge = renderAlertBadge(cardData.alerts);

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid="compact-trip-card"
      aria-label={`Trip: ${cardData.tripName}. ${cardData.currentStageLabel}. ${cardData.nextActionSummary || 'No pending actions.'}`}
      style={cardContainerStyle}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
    >
      {/* Header: Trip name + date range */}
      <div style={cardHeaderStyle}>
        <h3 style={tripNameStyle} data-testid="compact-trip-name">
          {cardData.tripName}
        </h3>
        <span style={dateRangeStyle} data-testid="compact-date-range">
          {formatDateRange(cardData.dateRange)}
        </span>
      </div>

      {/* Current stage indicator + alert badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={stageIndicatorStyle} data-testid="compact-stage-indicator">
          <StageIcon stageState={getStageState(cardData)} />
          {cardData.currentStageLabel}
        </span>
        {alertBadge}
      </div>

      {/* Next action summary (hidden for completed trips) */}
      {cardData.nextActionSummary && (
        <p style={nextActionStyle} data-testid="compact-next-action">
          {cardData.nextActionSummary}
        </p>
      )}

      {/* Progress indicator */}
      <div style={progressContainerStyle} data-testid="compact-progress">
        <div style={progressBarOuterStyle}>
          <div
            style={progressBarInnerStyle}
            data-testid="compact-progress-bar"
            role="progressbar"
            aria-valuenow={cardData.progress.completed}
            aria-valuemin={0}
            aria-valuemax={cardData.progress.total}
            aria-label={`${cardData.progress.completed} of ${cardData.progress.total} stages completed`}
          />
        </div>
        <span style={progressLabelStyle} data-testid="compact-progress-label">
          {cardData.progress.completed}/{cardData.progress.total} stages
        </span>
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────

/**
 * Determines the visual state for the stage icon.
 *
 * @param {Object} cardData - Computed compact card data
 * @returns {'completed' | 'active' | 'upcoming'}
 */
function getStageState(cardData) {
  if (cardData.progress.total > 0 && cardData.progress.completed === cardData.progress.total) {
    return 'completed';
  }
  if (cardData.progress.completed === 0) {
    return 'upcoming';
  }
  return 'active';
}

/**
 * Small icon indicating the stage state.
 *
 * @param {{ stageState: 'completed' | 'active' | 'upcoming' }} props
 * @returns {React.ReactElement}
 */
function StageIcon({ stageState }) {
  if (stageState === 'completed') {
    return (
      <span aria-hidden="true" style={{ fontSize: '12px', lineHeight: 1 }}>✓</span>
    );
  }
  if (stageState === 'active') {
    return (
      <span
        aria-hidden="true"
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: colors.info,
          display: 'inline-block',
        }}
      />
    );
  }
  // upcoming
  return (
    <span
      aria-hidden="true"
      style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        border: `2px solid ${colors.textMuted}`,
        display: 'inline-block',
        boxSizing: 'border-box',
      }}
    />
  );
}

/**
 * Renders the alert badge based on the number and type of alerts.
 * - Single alert: badge with alert type color
 * - Multiple alerts: count badge with most urgent type color
 * - No alerts: null
 *
 * @param {Array} alerts - Alert badge data
 * @returns {React.ReactElement|null}
 */
function renderAlertBadge(alerts) {
  if (!alerts || alerts.length === 0) return null;

  const mostUrgentType = alerts[0].type; // alerts are pre-sorted: critical first

  if (alerts.length === 1) {
    return (
      <span
        data-testid="compact-alert-badge"
        style={getAlertBadgeStyle(mostUrgentType)}
        aria-label={`1 ${mostUrgentType} alert`}
      >
        {mostUrgentType === 'critical' ? '!' : '⚠'}
      </span>
    );
  }

  return (
    <span
      data-testid="compact-alert-badge"
      style={getAlertBadgeStyle(mostUrgentType)}
      aria-label={`${alerts.length} alerts, most urgent: ${mostUrgentType}`}
    >
      {alerts.length}
    </span>
  );
}

export default CompactTripCard;
